import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ChevronRight } from 'lucide-react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const countries = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Spain', 'Italy', 'Japan', 'Mexico', 'Brazil', 'India', 'Other'];

const statesByCountry = {
  'United States': [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
    'Wisconsin', 'Wyoming', 'District of Columbia', 'Puerto Rico', 'Other'
  ],
  'Canada': [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
    'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
    'Quebec', 'Saskatchewan', 'Yukon', 'Other'
  ],
  'United Kingdom': ['England', 'Scotland', 'Wales', 'Northern Ireland'],
  'Australia': [
    'Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland',
    'South Australia', 'Tasmania', 'Victoria', 'Western Australia', 'Other'
  ],
  'Germany': [
    'Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hesse',
    'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia', 'Rhineland-Palatinate',
    'Saarland', 'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia', 'Other'
  ],
  'France': [
    'Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Brittany', 'Centre-Val de Loire',
    'Corsica', 'Grand Est', 'Hauts-de-France', 'Île-de-France', 'Normandy', 'Nouvelle-Aquitaine',
    'Occitanie', 'Pays de la Loire', 'Provence-Alpes-Côte d\'Azur', 'Other'
  ],
  'Spain': [
    'Andalusia', 'Aragon', 'Asturias', 'Balearic Islands', 'Basque Country', 'Canary Islands',
    'Cantabria', 'Castile and León', 'Castilla-La Mancha', 'Catalonia', 'Extremadura',
    'Galicia', 'La Rioja', 'Madrid', 'Murcia', 'Navarre', 'Valencia', 'Other'
  ],
  'Italy': [
    'Abruzzo', 'Aosta Valley', 'Apulia', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna',
    'Friuli Venezia Giulia', 'Lazio', 'Liguria', 'Lombardy', 'Marche', 'Molise', 'Piedmont',
    'Sardinia', 'Sicily', 'Trentino-South Tyrol', 'Tuscany', 'Umbria', 'Veneto', 'Other'
  ],
  'Japan': [
    'Hokkaido', 'Aomori', 'Iwate', 'Miyagi', 'Akita', 'Yamagata', 'Fukushima', 'Ibaraki',
    'Tochigi', 'Gunma', 'Saitama', 'Chiba', 'Tokyo', 'Kanagawa', 'Niigata', 'Toyama',
    'Ishikawa', 'Fukui', 'Yamanashi', 'Nagano', 'Gifu', 'Shizuoka', 'Aichi', 'Mie',
    'Shiga', 'Kyoto', 'Osaka', 'Hyogo', 'Nara', 'Wakayama', 'Tottori', 'Shimane',
    'Okayama', 'Hiroshima', 'Yamaguchi', 'Tokushima', 'Kagawa', 'Ehime', 'Kochi',
    'Fukuoka', 'Saga', 'Nagasaki', 'Kumamoto', 'Oita', 'Miyazaki', 'Kagoshima', 'Okinawa', 'Other'
  ],
  'Mexico': [
    'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
    'Chihuahua', 'Coahuila', 'Colima', 'Durango', 'Guanajuato', 'Guerrero', 'Hidalgo',
    'Jalisco', 'Mexico City', 'México', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León',
    'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa',
    'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas', 'Other'
  ],
  'Brazil': [
    'Acre', 'Alagoas', 'Amapá', 'Amazonas', 'Bahia', 'Ceará', 'Distrito Federal',
    'Espírito Santo', 'Goiás', 'Maranhão', 'Mato Grosso', 'Mato Grosso do Sul',
    'Minas Gerais', 'Pará', 'Paraíba', 'Paraná', 'Pernambuco', 'Piauí', 'Rio de Janeiro',
    'Rio Grande do Norte', 'Rio Grande do Sul', 'Rondônia', 'Roraima', 'Santa Catarina',
    'São Paulo', 'Sergipe', 'Tocantins', 'Other'
  ],
  'India': [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
    'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
    'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
    'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Other'
  ],
  'Other': ['N/A']
};

export default function LocationPicker() {
  const navigate = useNavigate();
  const { setLocationData } = useContext(AuthContext);
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [customState, setCustomState] = useState('');
  const [town, setTown] = useState('');
  const [autoDetected, setAutoDetected] = useState(false);

  const detectLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      setCountry(data.country_name || '');
      
      // Check if detected state is in our list, otherwise set to Other
      const detectedState = data.region || '';
      const countryStates = statesByCountry[data.country_name] || statesByCountry['Other'];
      if (countryStates.includes(detectedState)) {
        setState(detectedState);
      } else if (detectedState) {
        setState('Other');
        setCustomState(detectedState);
      }
      
      setTown(data.city || '');
      setAutoDetected(true);
    } catch (error) {
      console.error('Location detection failed:', error);
    }
  };

  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      navigate('/home');
    } else {
      detectLocation();
    }
  }, [navigate]);

  const handleStateChange = (value) => {
    setState(value);
    if (value !== 'Other') {
      setCustomState('');
    }
  };

  const handleContinue = () => {
    if (!country) {
      alert('Please select a country');
      return;
    }

    // Use custom state if "Other" is selected
    const finalState = state === 'Other' && customState ? customState : (state || 'N/A');
    
    const location = { country, state: finalState, town: town || 'N/A' };
    localStorage.setItem('userLocation', JSON.stringify(location));
    setLocationData(location);
    navigate('/home');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Subtle decorative elements */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 rounded-full blur-3xl" style={{ backgroundColor: 'var(--fy-background-secondary)' }}></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'var(--fy-surface-muted)' }}></div>
      </div>

      <div className="max-w-2xl w-full relative z-10 animate-slide-in">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl shadow-subtle mb-6 overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
            <img src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" alt="LikeKind" className="w-24 h-24 object-cover" />
          </div>
          <h1 className="text-5xl md:text-6xl font-black mb-3 tracking-tight" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
            <span className="text-gradient">LikeKind</span>
          </h1>
          <p className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
            Find your kind.
          </p>
          <p className="text-lg font-medium" style={{ color: 'var(--fy-text-secondary)' }}>A place to connect. Community starts here.</p>
        </div>

        <div className="rounded-3xl p-8 md:p-10 shadow-subtle-lg" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          {autoDetected && (
            <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>📍 Location detected! You can change it below.</p>
            </div>
          )}

          <div className="space-y-6">
            <div data-testid="country-select-container">
              <label className="block text-sm font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>Country</label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="country-select" className="w-full rounded-xl h-14 text-base font-semibold" style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}>
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {country && (
              <div data-testid="state-select-container">
                <label className="block text-sm font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>State / Region</label>
                <Select value={state} onValueChange={handleStateChange}>
                  <SelectTrigger data-testid="state-select" className="w-full rounded-xl h-14 text-base font-semibold" style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}>
                    <SelectValue placeholder="Select your state" />
                  </SelectTrigger>
                  <SelectContent>
                    {(statesByCountry[country] || statesByCountry['Other']).map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Custom state input when "Other" is selected */}
            {state === 'Other' && (
              <div data-testid="custom-state-container">
                <label className="block text-sm font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>Enter Your State / Region</label>
                <input
                  data-testid="custom-state-input"
                  type="text"
                  value={customState}
                  onChange={(e) => setCustomState(e.target.value)}
                  placeholder="Type your state or region name"
                  className="w-full rounded-xl px-4 h-14 text-base font-medium focus:outline-none"
                  style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
                />
              </div>
            )}

            <div data-testid="town-input-container">
              <label className="block text-sm font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>Town / City</label>
              <input
                data-testid="town-input"
                type="text"
                value={town}
                onChange={(e) => setTown(e.target.value)}
                placeholder="Enter your town or city"
                className="w-full rounded-xl px-4 h-14 text-base font-medium focus:outline-none"
                style={{ border: '1px solid var(--fy-border-subtle)', backgroundColor: 'var(--fy-background-primary)' }}
              />
            </div>
          </div>

          <Button
            data-testid="continue-button"
            onClick={handleContinue}
            className="w-full mt-8 h-14 text-lg font-bold rounded-full shadow-subtle"
            style={{ backgroundColor: 'var(--fy-accent-positive)', color: 'var(--fy-background-primary)' }}
          >
            Continue to LikeKind
            <ChevronRight className="ml-2 w-6 h-6" />
          </Button>
        </div>
      </div>
    </div>
  );
}